/**
 * sprint/useSprintState.js
 * ─────────────────────────────────────────────────────────────────────────────
 * State hook for the Sprint Board.
 * Fetches real sprint data from the backend on mount and exposes
 * sprint selection, section collapse, and modal open/close state.
 */
import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { fetchSprints } from '../api/projectManagementApi';

export default function useSprintState() {
    const [sprints, setSprints] = useState([]);
    const [sprintsLoading, setSprintsLoading] = useState(true);
    const [selectedSprint, setSelectedSprint] = useState(null);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [isReportOpen, setIsReportOpen] = useState(false);
    const [collapsedSections, setCollapsedSections] = useState({});

    // ── Load sprints from backend ─────────────────────────────────────────────
    const loadSprints = useCallback(async () => {
        setSprintsLoading(true);
        try {
            const { data } = await fetchSprints();
            if (data.success) {
                const list = data.sprints || [];
                setSprints(list);

                // Default to the active sprint, else the most recent planned sprint
                const active = list.find(s => s.status === 'active');
                const planned = [...list]
                    .filter(s => s.status === 'planned')
                    .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))[0];
                const fallback = list[0] || null;

                setSelectedSprint(prev => prev ?? (active || planned || fallback));
            }
        } catch (err) {
            // If no sprints exist yet, stay with null — the header handles this gracefully
            const msg = err?.response?.data?.message;
            if (err?.response?.status !== 404) {
                message.error(msg || 'Failed to load sprints.');
            }
        } finally {
            setSprintsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadSprints();
    }, [loadSprints]);

    // ── Refresh a single sprint after settings save ───────────────────────────
    const refreshSelectedSprint = useCallback(async () => {
        if (!selectedSprint?._id) return;
        try {
            const { data } = await fetchSprints();
            if (data.success) {
                const updated = data.sprints.find(s => s._id === selectedSprint._id);
                if (updated) setSelectedSprint(updated);
                setSprints(data.sprints);
            }
        } catch { /* silent */ }
    }, [selectedSprint]);

    const toggleSection = (key) => {
        setCollapsedSections(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const isSectionCollapsed = (key) => !!collapsedSections[key];

    return {
        sprints,
        sprintsLoading,
        selectedSprint,
        setSelectedSprint,
        loadSprints,
        refreshSelectedSprint,
        isSettingsOpen,
        setIsSettingsOpen,
        isReportOpen,
        setIsReportOpen,
        toggleSection,
        isSectionCollapsed,
    };
}
